import { z } from 'zod';
import { CodeEvidenceSchema } from './diagram.js';
// ============= Network Configuration Analyzer Types =============
/**
 * Environment variable source
 */
export const EnvSource = z.enum([
    'env-file', // .env, .env.local, .env.production
    'docker', // Docker Compose env_file or environment
    'kubernetes', // K8s ConfigMap/Secret
    'vercel', // vercel.json or Vercel dashboard
    'process', // process.env references in code
    'hardcoded', // Hardcoded in config files
]);
/**
 * Service type classification
 */
export const ServiceType = z.enum([
    'web-frontend',
    'api-server',
    'worker',
    'database',
    'cache',
    'queue',
    'storage',
    'external-api',
    'reverse-proxy',
    'load-balancer',
    'unknown',
]);
/**
 * Connection type between services
 */
export const ConnectionType = z.enum([
    'http',
    'https',
    'tcp',
    'database',
    'redis',
    'amqp',
    'grpc',
    'websocket',
    'internal',
]);
// ============= Environment Variable Schema =============
/**
 * Detected environment variable
 */
export const EnvVariableSchema = z.object({
    /** Variable name */
    name: z.string(),
    /** Default value if any */
    defaultValue: z.string().optional(),
    /** Current value (may be redacted for secrets) */
    value: z.string().optional(),
    /** Whether this is likely a secret */
    isSecret: z.boolean(),
    /** Whether this is required */
    isRequired: z.boolean(),
    /** Source where defined */
    source: EnvSource,
    /** Description/comment if available */
    description: z.string().optional(),
    /** Related URL/connection string */
    relatedUrl: z.string().optional(),
    /** Files where this var is used */
    usedIn: z.array(z.string()),
    /** Code evidence */
    evidence: z.array(CodeEvidenceSchema),
});
// ============= Docker Compose Service Schema =============
/**
 * Docker Compose service from compose file
 */
export const DockerComposeServiceSchema = z.object({
    /** Service name */
    name: z.string(),
    /** Docker image */
    image: z.string().optional(),
    /** Build context if building */
    buildContext: z.string().optional(),
    /** Exposed ports */
    ports: z.array(z.object({
        host: z.number(),
        container: z.number(),
        protocol: z.enum(['tcp', 'udp']).optional(),
    })),
    /** Environment variables */
    environment: z.array(z.object({
        name: z.string(),
        value: z.string().optional(),
        fromEnvFile: z.boolean(),
    })),
    /** Dependencies */
    dependsOn: z.array(z.string()),
    /** Network(s) connected to */
    networks: z.array(z.string()),
    /** Volumes mounted */
    volumes: z.array(z.object({
        source: z.string(),
        target: z.string(),
        readOnly: z.boolean().optional(),
    })),
    /** Service type classification */
    serviceType: ServiceType,
    /** Health check if defined */
    healthCheck: z.object({
        test: z.string(),
        interval: z.string().optional(),
        timeout: z.string().optional(),
    }).optional(),
    /** Source file */
    sourceFile: z.string(),
    /** Code evidence */
    evidence: z.array(CodeEvidenceSchema),
});
// ============= Kubernetes Resource Schema =============
/**
 * Kubernetes resource
 */
export const K8sResourceSchema = z.object({
    /** Resource kind */
    kind: z.enum([
        'Deployment',
        'Service',
        'ConfigMap',
        'Secret',
        'Ingress',
        'StatefulSet',
        'DaemonSet',
        'Job',
        'CronJob',
        'Pod',
    ]),
    /** Resource name */
    name: z.string(),
    /** Namespace */
    namespace: z.string().optional(),
    /** Labels */
    labels: z.record(z.string()),
    /** Container specs for workloads */
    containers: z.array(z.object({
        name: z.string(),
        image: z.string(),
        ports: z.array(z.number()),
        envVars: z.array(z.object({
            name: z.string(),
            value: z.string().optional(),
            fromConfigMap: z.string().optional(),
            fromSecret: z.string().optional(),
        })),
    })).optional(),
    /** Service type and ports for Service resources */
    serviceSpec: z.object({
        type: z.enum(['ClusterIP', 'NodePort', 'LoadBalancer', 'ExternalName']),
        ports: z.array(z.object({
            port: z.number(),
            targetPort: z.number(),
            protocol: z.string().optional(),
        })),
        selector: z.record(z.string()),
    }).optional(),
    /** Ingress rules */
    ingressRules: z.array(z.object({
        host: z.string().optional(),
        paths: z.array(z.object({
            path: z.string(),
            serviceName: z.string(),
            servicePort: z.number(),
        })),
    })).optional(),
    /** ConfigMap/Secret data keys */
    dataKeys: z.array(z.string()).optional(),
    /** Source file */
    sourceFile: z.string(),
    /** Code evidence */
    evidence: z.array(CodeEvidenceSchema),
});
// ============= Network Service Schema =============
/**
 * Detected network service
 */
export const NetworkServiceSchema = z.object({
    /** Service ID */
    id: z.string(),
    /** Service name */
    name: z.string(),
    /** Service type */
    type: ServiceType,
    /** Host/hostname */
    host: z.string(),
    /** Port */
    port: z.number().optional(),
    /** Protocol */
    protocol: ConnectionType,
    /** Base URL if applicable */
    baseUrl: z.string().optional(),
    /** Whether this is internal to the cluster/network */
    isInternal: z.boolean(),
    /** Whether this is a managed service (RDS, Redis Cloud, etc.) */
    isManaged: z.boolean().optional(),
    /** Provider (AWS, GCP, Azure, etc.) */
    provider: z.string().optional(),
    /** Environment variable providing connection */
    envVar: z.string().optional(),
    /** Source (docker, k8s, env, code) */
    source: z.enum(['docker', 'kubernetes', 'env', 'code', 'config']),
    /** Code evidence */
    evidence: z.array(CodeEvidenceSchema),
});
// ============= Network Connection Schema =============
/**
 * Connection between services
 */
export const NetworkConnectionSchema = z.object({
    /** Connection ID */
    id: z.string(),
    /** Source service ID */
    from: z.string(),
    /** Target service ID */
    to: z.string(),
    /** Connection type */
    type: ConnectionType,
    /** Port used */
    port: z.number().optional(),
    /** Whether connection is encrypted */
    isEncrypted: z.boolean().optional(),
    /** Authentication method */
    authMethod: z.enum(['none', 'api-key', 'jwt', 'oauth', 'mtls', 'password']).optional(),
    /** Code evidence */
    evidence: z.array(CodeEvidenceSchema),
});
// ============= Network Topology Schema =============
/**
 * Complete network topology
 */
export const NetworkTopologySchema = z.object({
    /** All detected services */
    services: z.array(NetworkServiceSchema),
    /** All connections between services */
    connections: z.array(NetworkConnectionSchema),
    /** Environment variables */
    envVariables: z.array(EnvVariableSchema),
    /** Docker services if detected */
    dockerServices: z.array(DockerComposeServiceSchema).optional(),
    /** K8s resources if detected */
    k8sResources: z.array(K8sResourceSchema).optional(),
    /** Networks/namespaces */
    networks: z.array(z.object({
        name: z.string(),
        type: z.enum(['docker', 'kubernetes', 'vpc', 'unknown']),
        services: z.array(z.string()),
    })),
});
// ============= Network Config Analyzer Result =============
/**
 * Complete result from network config analysis
 */
export const NetworkConfigAnalyzerResultSchema = z.object({
    /** Network topology */
    topology: NetworkTopologySchema,
    /** Summary statistics */
    summary: z.object({
        totalServices: z.number(),
        totalConnections: z.number(),
        totalEnvVariables: z.number(),
        secretCount: z.number(),
        internalServices: z.number(),
        externalServices: z.number(),
        byServiceType: z.record(z.number()),
        byConnectionType: z.record(z.number()),
    }),
    /** Potential issues detected */
    issues: z.array(z.object({
        type: z.enum(['missing-env', 'insecure-connection', 'unused-service', 'circular-dependency']),
        severity: z.enum(['error', 'warning', 'info']),
        message: z.string(),
        relatedService: z.string().optional(),
        relatedEnvVar: z.string().optional(),
    })),
});
//# sourceMappingURL=networkConfigAnalyzer.js.map